import * as Immutable from 'immutable';
import * as React from 'react';
import { useEffect } from 'react';

import { localeData } from '../../locales/data';
import { addModifiers } from '../../shared/helpers';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import {
  displayAgreementContent,
  IS_READ_AGREEMENT_FIELD,
  useIsReadAllContent,
  UserAgreementsButtons,
  UserAgreementsList,
} from './AgreementsHelpers';
import { Controller } from 'react-hook-form';
import { HookFieldError } from '../../containers/Account/FormFields/HookFieldSet';
import { Loader } from 'semantic-ui-react';
import { UserAgreementsContext } from './UserAgreementsTypes';
import { changeFormField } from '../../actions/AccountActions/AccountWizardActions';
import { useSelector } from 'react-redux';
import { useWindowWidth } from '@react-hook/window-size';
import { MOBILE_BREAKPOINTS } from '../../shared/constants';
import { use100vh } from 'react-div-100vh';
import { AGREEMENTS_FORM_STAGES } from '../../reducers/AccountReducer/InitialAccountWizardData';
import { agreementFormStageSelector } from '../../reducers/AccountReducer/AccountWizardSelector';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

export const UserAgreementsMain = ({ context }: { context: UserAgreementsContext }) => {
  const formStage = useSelector(agreementFormStageSelector);
  if (formStage === AGREEMENTS_FORM_STAGES.ReadAgreement) {
    return <ReadAgreementStage context={context} />;
  }

  if (formStage === AGREEMENTS_FORM_STAGES.SubmitAgreement) {
    return <SubmitAgreementStage context={context} />;
  }
  throw new Error('Unknown User Agreement form stage');
};

const ReadAgreementStage = ({
  context: {
    handleAccepted,
    handleBack,
    modifiers = [],
    form,

    userAgreementQuery,
    termsQuery,
    challengeQuery,
    competitionAgreementQuery,
    isShowCompetitionAgreement,
    isShowAgree,
  },
}: {
  context: UserAgreementsContext;
}) => {
  const desktopContentRef = React.useRef<HTMLDivElement | null>(null);
  const mobileContentRef = React.useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();
  const { isReadAllContent: readDesktop } = useIsReadAllContent(desktopContentRef);
  const { isReadAllContent: readMobile } = useIsReadAllContent(mobileContentRef);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= MOBILE_BREAKPOINTS.table.width;

  useEffect(() => {
    const setReadContent = () => {
      form.setValue(IS_READ_AGREEMENT_FIELD, true, { shouldValidate: true });
      dispatch(changeFormField({ name: IS_READ_AGREEMENT_FIELD, value: true }));
    };
    if (isMobile && readMobile) {
      setReadContent();
    }
    if (!isMobile && readDesktop) {
      setReadContent();
    }
  }, [readDesktop, readMobile, isMobile, form]);

  const mobileFormContentHeight = useMobileFormContentHeight();

  const isFetching = userAgreementQuery.isFetching || termsQuery.isFetching || challengeQuery.isFetching;
  const isError = userAgreementQuery.isError || termsQuery.isError || challengeQuery.isError;
  const isSuccess = userAgreementQuery.isSuccess && termsQuery.isSuccess && challengeQuery.isSuccess;

  return (
    <form onSubmit={handleAccepted}>
      <div style={{ maxHeight: mobileFormContentHeight }} ref={mobileContentRef} className="agreements__form-content">
        <header className="agreements__header">
          <h3 className="agreements__title">
            <FormattedMessageHelper id={'userAgreement.title'} />
          </h3>
        </header>
        <div className={addModifiers('agreements', [...modifiers].filter(Boolean))}>
          <div className="agreements__instruction">
            <p>
              <FormattedMessageHelper id={'userAgreement.pleaseReadAgreement'} />
            </p>
          </div>

          <Controller
            shouldUnregister={false}
            name={IS_READ_AGREEMENT_FIELD}
            rules={{ validate: value => (value ? undefined : 'userAgreement.readAgreementError') }}
            control={form.control}
            render={controller => (
              <div>
                <div className="agreements__content" ref={desktopContentRef}>
                  <div className="agreements__content-scroll">
                    <div className="agreements__content-text">
                      {isFetching && (
                        <div className="loader">
                          <Loader active={true} inline="centered" size="massive" />
                        </div>
                      )}

                      {isError && (
                        <div className="agreements__content full-height">
                          <div className="agreements__content-scroll full-height">
                            <div className="agreements__not-available full-height">
                              <FormattedMessageHelper
                                tagName="span"
                                id="agreements.available"
                                defaultMessage={localeData.en['agreements.available']}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {isSuccess && (
                        <>
                          <h3>{userAgreementQuery.data.title}</h3>
                          {displayAgreementContent(Immutable.fromJS(userAgreementQuery.data) as Immutable.Map<string, any>)}

                          {isShowAgree && (
                            <>
                              <h3>{termsQuery.data.title}</h3>
                              {displayAgreementContent(Immutable.fromJS(termsQuery.data) as Immutable.Map<string, any>)}
                            </>
                          )}
                          <h3>{challengeQuery.data.title}</h3>
                          {displayAgreementContent(Immutable.fromJS(challengeQuery.data) as Immutable.Map<string, any>)}

                          {competitionAgreementQuery.isSuccess && isShowCompetitionAgreement && (
                            <>
                              <h3>{competitionAgreementQuery.data.title}</h3>
                              {displayAgreementContent(Immutable.fromJS(competitionAgreementQuery.data) as Immutable.Map<string, any>)}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={'agreements__text-error-desktop'}>
                  <HookFieldError control={form.control} controller={controller} />
                </div>
              </div>
            )}
          />
        </div>
      </div>
      <UserAgreementsButtons control={form.control} handleBack={handleBack} />
    </form>
  );
};

const SubmitAgreementStage = ({
  context: { handleAccepted, handleBack, agreementsList, modifiers = [], form },
}: {
  context: UserAgreementsContext;
}) => {
  const mobileFormContentHeight = useMobileFormContentHeight();
  return (
    <form onSubmit={handleAccepted}>
      <div style={{ maxHeight: mobileFormContentHeight }} className="agreements__form-content">
        <header className="agreements__header">
          <h3 className="agreements__title">
            <FormattedMessageHelper id={'userAgreement.title'} />
          </h3>
        </header>
        <div className={addModifiers('agreements', [...modifiers, 'agreement-read'].filter(Boolean))}></div>
        <UserAgreementsList agreementsList={agreementsList} control={form.control} />
      </div>

      <UserAgreementsButtons control={form.control} handleBack={handleBack} />
    </form>
  );
};

const USER_AGREEMENTS_MOBILE_FORM_GAP = 180;

const useMobileFormContentHeight = () => {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= MOBILE_BREAKPOINTS.table.width;

  const windowHeight = use100vh() ?? 0;
  return isMobile ? windowHeight - USER_AGREEMENTS_MOBILE_FORM_GAP : undefined;
};
